import { ALL_BRANCHES, ContextResolution, ContextSession, OnboardingStatus, TenantContext } from './types';

export type ResolveContextInput = {
  session: ContextSession | null;
  status: OnboardingStatus | null;
  cookie: TenantContext | null;
  currentPath: string;
  hasOrgLeaf?: boolean;
};

function redirect(to: string): ContextResolution {
  return { kind: 'redirect', to };
}

/**
 * Cookie is the authority for in-app tenancy. Pure so it stays exhaustively
 * testable; the caller persists `context` to the cookie when it renders.
 */
export function resolveContext(input: ResolveContextInput): ContextResolution {
  const { session, status, cookie, currentPath, hasOrgLeaf = false } = input;

  if (!session) {
    return redirect(`/sign-in?returnTo=${encodeURIComponent(currentPath)}`);
  }
  if (!session.emailVerified) {
    return redirect('/verify-email');
  }
  if (!status || !status.hasOrg || !status.hasRestaurant || !status.hasBranch) {
    return redirect('/onboarding');
  }

  const canAggregate = session.organizationRole === 'owner' || session.organizationRole === 'admin';
  // Everyone defaults to a concrete branch, owners included. `/b/all` resolves
  // `restaurantBranchId` to null server-side and `Orders.restaurantBranchId` is NOT
  // NULL, so defaulting an owner into the aggregate left them unable to take a POS
  // order until they picked a branch by hand. Aggregate is opt-in via the switcher;
  // `canAggregate` below still decides whether a cookie may *stay* on it.
  const fallback: TenantContext = {
    orgSlug: status.organizationSlug!,
    restaurantId: status.firstRestaurantId!,
    branchId: status.firstBranchId!,
  };

  if (hasOrgLeaf) {
    // /org-settings and /profile are org-scoped only; restaurant/branch of the
    // cookie are irrelevant here but the org must match.
    if (cookie?.orgSlug === status.organizationSlug) {
      return { kind: 'render', context: cookie };
    }
    return { kind: 'render', context: fallback };
  }

  if (cookie && cookie.orgSlug === status.organizationSlug && !(cookie.branchId === ALL_BRANCHES && !canAggregate)) {
    return { kind: 'render', context: cookie };
  }

  return { kind: 'render', context: fallback };
}
